import { useCallback, useEffect, useState } from "react";
import { Invitation, InvitationDTO } from "types/invitation.type";
import { User } from "types/user.type";
import { Role } from "types/enums";
import {
  ShieldCheckIcon,
  UserPlusIcon,
  UsersIcon,
  TrashIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import PrimaryButton from "components/buttons/PrimaryButton";
import { DotMenu } from "components/DotMenu";
import { cn } from "helpers/classNames";
import { useNotification } from "hooks/useNotification";
import { useUsers, useToggleUserDisabled } from "api/user.api";
import {
  useDeleteInvitation,
  useGetInvitations,
  useInviteUser,
} from "api/invitation.api";
import { useForm } from "react-hook-form";
import { PageLoader } from "components/PageLoader";
import Modal from "components/Modal";
import { AddUserModal } from "./AddUserModal";
import { Card, CardBody, CardHeader, EmptyState, StatusBadge } from "./ui";

const RoleBadge = ({ role }: { role: Role }) =>
  role === Role.Admin ? (
    <StatusBadge color="blue" icon={ShieldCheckIcon}>
      Administratör
    </StatusBadge>
  ) : (
    <StatusBadge color="slate">Operatör</StatusBadge>
  );

const initials = (value: string) =>
  value
    .replace(/@.*/, "")
    .split(/[.\s_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || value[0]?.toUpperCase();

export const UsersTab = () => {
  const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState(false);
  const notification = useNotification();

  const {
    data: users,
    isLoading: isLoadingUsers,
    isError: isUsersError,
    refetch: refetchUsers,
  } = useUsers({}, { includeDisabledUsers: true });

  const { mutate: toggleUserDisabled } = useToggleUserDisabled();
  const {
    data: invitations,
    isLoading: isLoadingInvitations,
    isError: isInvitationsError,
    refetch: refetchInvitations,
  } = useGetInvitations();

  // A failed fetch must not masquerade as an empty organization — track it so
  // the empty state stays reserved for genuinely successful, data-less reads.
  const hasFetchError = isUsersError || isInvitationsError;

  const {
    mutate: inviteUser,
    isPending: isInvitingUser,
    isSuccess: isInviteUserSuccess,
    reset: resetInviteUser,
  } = useInviteUser();

  const {
    register: registerInvitation,
    handleSubmit: handleInvitationSubmit,
    reset: resetInvitation,
    formState: { errors: invitationErrors },
  } = useForm<InvitationDTO>({ defaultValues: { role: Role.Operator } });

  const onInvitationSubmit = handleInvitationSubmit((data: InvitationDTO) =>
    inviteUser(data),
  );

  const onInvitationModalClose = useCallback(() => {
    setIsInviteUserModalOpen(false);
    setTimeout(() => {
      resetInvitation();
      resetInviteUser();
    }, 300); // Wait for modal to close
  }, [resetInvitation, resetInviteUser]);

  useEffect(() => {
    document.title = "Användare";
  }, []);

  useEffect(() => {
    if (isInviteUserSuccess) {
      onInvitationModalClose();
    }
  }, [isInviteUserSuccess, onInvitationModalClose]);

  // Surface fetch failures with a toast. `notification` is recreated on every
  // render, so depend on the error flag alone to fire once per failure.
  useEffect(() => {
    if (hasFetchError) {
      notification.error("Det gick inte att hämta användarna. Försök igen.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFetchError]);

  // Delete invitation
  const { mutate: deleteInvitation } = useDeleteInvitation();

  if (isLoadingUsers || isLoadingInvitations) {
    return <PageLoader />;
  }

  // Deactivated users sink to the bottom; within each group sort by role
  // (highest first) then name (A–Z).
  const userList = [...(users ?? [])].sort((a, b) => {
    if (a.isDisabled !== b.isDisabled) return a.isDisabled ? 1 : -1;
    if (a.role !== b.role) return b.role - a.role;
    return (a.name || a.email).localeCompare(b.name || b.email, "sv");
  });
  const invitationList = invitations ?? [];
  // Only treat the organization as empty when both reads succeeded with no data.
  const isEmpty =
    !hasFetchError && userList.length === 0 && invitationList.length === 0;

  return (
    <Card>
      <Modal
        isOpen={isInviteUserModalOpen}
        onClose={onInvitationModalClose}
        title="Lägg till användare"
        actionText="Skicka inbjudan"
        actionHandler={onInvitationSubmit}
        isLoading={isInvitingUser}
        loadingText="Skickar inbjudan..."
      >
        <AddUserModal register={registerInvitation} errors={invitationErrors} />
      </Modal>

      <CardHeader
        icon={UsersIcon}
        title="Användare"
        description="Personer med åtkomst till er organisation."
        action={
          <PrimaryButton
            onClick={() => setIsInviteUserModalOpen(true)}
            size="small"
          >
            <UserPlusIcon className="mr-1.5 h-4 w-4" /> Ny användare
          </PrimaryButton>
        }
      />

      {hasFetchError ? (
        <CardBody>
          <EmptyState
            icon={ExclamationTriangleIcon}
            title="Det gick inte att hämta användarna"
            description="Kontrollera din anslutning och försök igen."
            action={
              <PrimaryButton
                onClick={() => {
                  if (isUsersError) refetchUsers();
                  if (isInvitationsError) refetchInvitations();
                }}
                size="small"
              >
                Försök igen
              </PrimaryButton>
            }
          />
        </CardBody>
      ) : isEmpty ? (
        <CardBody>
          <EmptyState
            icon={UsersIcon}
            title="Inga användare än"
            description="Bjud in kollegor så att de kan logga in på ert konto."
            action={
              <PrimaryButton
                onClick={() => setIsInviteUserModalOpen(true)}
                size="small"
              >
                <UserPlusIcon className="mr-1.5 h-4 w-4" /> Ny användare
              </PrimaryButton>
            }
          />
        </CardBody>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="py-3 pl-5 pr-3 font-semibold sm:pl-6">
                  Användare
                </th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-3 py-3 font-semibold">Kontotyp</th>
                <th className="py-3 pl-3 pr-5 sm:pr-6" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {userList.map(({ id, email, name, role, isDisabled }: User) => (
                <tr
                  key={email}
                  className={cn(
                    "hover:bg-slate-50/70",
                    isDisabled && "bg-rose-50/40",
                  )}
                >
                  <td className="py-3 pl-5 pr-3 sm:pl-6">
                    <div
                      className={cn(
                        "flex items-center gap-3",
                        isDisabled && "opacity-60",
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ring-1 ring-inset",
                          isDisabled
                            ? "bg-rose-50 text-rose-600 ring-rose-100"
                            : "bg-blue-50 text-blue-700 ring-blue-100",
                        )}
                      >
                        {initials(name || email)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900">
                          {name || email}
                        </p>
                        {name && (
                          <p className="truncate text-xs text-slate-500">
                            {email}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {isDisabled ? (
                      <StatusBadge color="rose">Avaktiverad</StatusBadge>
                    ) : (
                      <StatusBadge color="green" dot>
                        Aktiv
                      </StatusBadge>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <RoleBadge role={role} />
                  </td>
                  <td className="py-3 pl-3 pr-5 text-right sm:pr-6">
                    <div className="flex justify-end">
                      <DotMenu
                        isDiscreete
                        items={[
                          {
                            label: isDisabled ? "Aktivera" : "Avaktivera",
                            icon: isDisabled ? (
                              <CheckCircleIcon className="h-4 w-4 text-green-600" />
                            ) : (
                              <NoSymbolIcon className="h-4 w-4 text-slate-500" />
                            ),
                            onClick: () =>
                              toggleUserDisabled({ id, isDisabled: !isDisabled }),
                          },
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              ))}

              {invitationList.map(({ email, id, role, name }: Invitation) => (
                <tr key={id} className="hover:bg-slate-50/70">
                  <td className="py-3 pl-5 pr-3 sm:pl-6">
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600 ring-1 ring-inset ring-amber-100">
                        <EnvelopeIcon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900">
                          {name || email}
                        </p>
                        {name && (
                          <p className="truncate text-xs text-slate-500">
                            {email}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <StatusBadge color="amber" dot>
                      Inbjuden
                    </StatusBadge>
                  </td>
                  <td className="px-3 py-3">
                    <RoleBadge role={role} />
                  </td>
                  <td className="py-3 pl-3 pr-5 text-right sm:pr-6">
                    <button
                      type="button"
                      onClick={() => deleteInvitation(id)}
                      className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-700"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Ta bort
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};
